import React from "react";
import {
  Select,
  MenuItem,
  ListSubheader,
  Box,
  Typography,
} from "@mui/material";

// ==================== FILTER PRODUCTS BY DELIVERY TYPE ====================
// This helper filters cart items based on delivery type
// For combo products, it checks parent_product tags
// For normal products, it checks products tags
export function filterProductsByDeliveryType(cartItems, deliveryType) {
  if (!cartItems || cartItems.length === 0) return [];
  
  return cartItems.filter((item) => {
    // Check if it's a combo product
    const isCombo = (item?.parent_product?.combo_price || 0) > 0;
    
    // Get the appropriate product tags
    const productTags = isCombo 
      ? item?.parent_product?.product_tags 
      : item?.products?.product_tags;
    
    if (!productTags) return false;
    
    // Filter based on delivery type
    if (deliveryType === "DOOR_STEP_DELIVERY") {
      return productTags.door_step_delivery === 1;
    }
    if (deliveryType === "PICK_UP") {
      return productTags.pick_up === 1;
    }
    return true;
  });
}

// ==================== DELIVERY TAB OPTIONS HELPER ====================
// This function checks parent_product for combo products
// Matches the exact logic from your Cart.jsx useEffect
export function getDeliveryTabOptions(cartItems) {
  if (!cartItems || cartItems.length === 0) {
    return {
      door_step_delivery: false,
      pick_up: false,
      door_step_products: new Set(),
      pick_up_products: new Set(),
      showDeliveryTypeCount: false,
    };
  }

  const result = cartItems.reduce(
    (acc, p) => {
      const isCombo = (p?.parent_product?.combo_price || 0) > 0;
      // Use parent_id for combos, product_id for regular items
      const uniqueId = isCombo && p?.parent_id 
        ? `combo_${p.parent_id}` 
        : `product_${p?.products?.id}`;

      if (isCombo) {
        if (p?.parent_product?.product_tags?.door_step_delivery === 1) {
          acc.door_step_delivery = true;
          acc.door_step_products.add(uniqueId);
        }
        if (p?.parent_product?.product_tags?.pick_up === 1) {
          acc.pick_up = true;
          acc.pick_up_products.add(uniqueId);
        }
      } else {
        if (p?.products?.product_tags?.door_step_delivery === 1) {
          acc.door_step_delivery = true;
          acc.door_step_products.add(uniqueId);
        }
        if (p?.products?.product_tags?.pick_up === 1) {
          acc.pick_up = true;
          acc.pick_up_products.add(uniqueId);
        }
      }
      return acc;
    },
    {
      door_step_delivery: false,
      pick_up: false,
      door_step_products: new Set(),
      pick_up_products: new Set(),
    }
  );

  // Check if products are different between delivery types
  const areEqual = (setA, setB) => {
    if (setA.size !== setB.size) return false;
    for (let item of setA) {
      if (!setB.has(item)) return false;
    }
    return true;
  };

  const showDeliveryCount = !areEqual(
    result.door_step_products,
    result.pick_up_products
  );

  return {
    ...result,
    showDeliveryTypeCount: showDeliveryCount,
  };
}

// ==================== PICKUP LOCATION HELPER ====================
// Transform cart data for pickup location dropdown
// This uses the flat shop_id structure from your actual data
function transformCartData(cartData, shops) {
  const allShopIds = shops.map((s) => String(s.shop_details_id));
  
  return cartData.map((item) => {
    // Check if shop_id exists at different levels
    // Priority: item.shop_id (flat) > parent_product.product_tags.shop_id > products.product_tags.shop_id
    let shopId;
    
    if (item.shop_id) {
      // Flat structure (current working case)
      shopId = item.shop_id;
    } else {
      // Nested structure (for compatibility with Expo data)
      const productTags = item.parent_product?.product_tags || item.products?.product_tags;
      shopId = productTags?.shop_id;
    }
    
    let availableIn;
    
    // If shop_id is null, empty, or not set, available in all shops
    if (!shopId || shopId === "" || shopId === null) {
      availableIn = allShopIds;
    } else {
      // Parse comma-separated shop IDs
      availableIn = shopId
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id !== "");
    }
    
    return {
      name: item.product_name || item.products?.product_name,
      id: item.product_id || item.products?.id || item.cart_id,
      availableIn,
    };
  });
}

// ==================== PICKUP LOCATION DROPDOWN COMPONENT ====================
const PickupLocationDropdown = ({ cartItems, shops, value, onChange, sx }) => {
  const cartItemsTransformed = transformCartData(cartItems, shops);
  
  // Helper to get shop availability
  const getShopAvailability = (shopId) => {
    const shopIdStr = String(shopId);
    const unavailable = cartItemsTransformed?.filter(
      (item) => !item?.availableIn.includes(shopIdStr)
    );
    return {
      isFullyAvailable: unavailable.length === 0,
      unavailableItems: unavailable,
    };
  };

  const fullyAvailableShops = shops.filter(
    (shop) => getShopAvailability(shop.shop_details_id)?.isFullyAvailable
  );
  const partiallyAvailableShops = shops.filter(
    (shop) => !getShopAvailability(shop.shop_details_id)?.isFullyAvailable
  );

  return (
    <Select
      value={value}
      onChange={onChange}
      displayEmpty
      fullWidth
      sx={sx}
      renderValue={(selected) => {
        if (!selected) return "--SELECT--";
        const shop = shops.find(
          (s) => String(s.shop_details_id) === String(selected)
        );
        return shop ? shop.shop_name : "--SELECT--";
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            backgroundColor: "white",
            fontSize: "16px",
            fontWeight: 500,
            color: "var(--neutral-black)",
          },
        },
      }}
    >
      <MenuItem value="">
        <em>--SELECT--</em>
      </MenuItem>

      {fullyAvailableShops.length > 0 && (
        <ListSubheader sx={{ color: "green", fontWeight: 600 }}>
          ✓ All Items Available
        </ListSubheader>
      )}
      {fullyAvailableShops.map((shop) => (
        <MenuItem
          sx={{ pl: 3 }}
          key={shop.shop_details_id}
          value={String(shop.shop_details_id)}
        >
          {shop.shop_name}
        </MenuItem>
      ))}
      {partiallyAvailableShops.length > 0 && (
        <ListSubheader sx={{ color: "#b26a00", fontWeight: 600 }}>
          ⚠ Some Items Unavailable
        </ListSubheader>
      )}
      {partiallyAvailableShops?.map((shop) => {
        const { unavailableItems } = getShopAvailability(shop.shop_details_id);
        return (
          <MenuItem
            disabled
            sx={{ pl: 3 }}
            key={shop.shop_details_id}
            value={String(shop.shop_details_id)}
          >
            <Box>
              <Typography component="span" sx={{ fontWeight: 500 }}>
                {shop.shop_name}
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: "12px",
                  color: "#b26a00",
                  pl: 1,
                }}
              >
                ({unavailableItems.length} unavailable)
              </Typography>
              <Box
                sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}
              >
                {unavailableItems.map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      px: 1,
                      py: 0.2,
                      border: "1px solid #ffe082",
                      borderRadius: "8px",
                      fontSize: "10px",
                      color: "#b26a00",
                      mr: 0.5,
                    }}
                  >
                    {item.name}
                  </Box>
                ))}
              </Box>
            </Box>
          </MenuItem>
        );
      })}
    </Select>
  );
};

export default PickupLocationDropdown;